import { getAbsoluteUrl } from '@tmapy/utils';
import { LAYER_TYPE } from '../constants.js';
import { hrefFromURL } from '../utils/hrefFromURL.js';
import { MapServiceType } from './types.js';
import { transformGridParams } from './transformGridParams.js';
const mapServiceLayerIdsIgnored = (node) => {
    if (node.mapServiceLayerIds) {
        console.warn(`[transformLayerConfigFromTinker] ignoring mapServiceLayerIds`, node);
    }
};
const getVectorFormat = (format) => {
    if (format === 'geojson' || format === 'topojson')
        return format;
    return 'empty';
};
export const transformLayerConfigFromTinker = ({ node, }) => {
    const legend = node.legendImageLink || node.legendExternalLink
        ? {
            imageSrc: node.legendImageLink,
            link: node.legendExternalLink,
        }
        : undefined;
    const routerParams = node.mapService.routerParams;
    const mapboxStyle = node.mapBoxStyle;
    const mapBoxStyleLegend = node.mapBoxStyleLegend;
    switch (node.mapService.type) {
        case MapServiceType.WFS:
        case MapServiceType.GeoCode: {
            return null;
        }
        case MapServiceType.FeatureArcGIS: {
            mapServiceLayerIdsIgnored(node);
            return {
                type: LAYER_TYPE.FEATURE_ARCGIS,
                id: node.nameId,
                auth: node.mapService.isAuth,
                routerParams,
                legend,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions,
                    format: getVectorFormat(node.mapService.format),
                    params: node.mapService,
                },
            };
        }
        case MapServiceType.ImageArcGISRest: {
            return {
                type: LAYER_TYPE.IMAGE_ARCGIS_REST,
                id: node.nameId,
                legend,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions,
                    params: {
                        layers: node.mapServiceLayerIds ? `show:${node.mapServiceLayerIds}` : undefined,
                    },
                },
                auth: node.mapService.isAuth,
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.ImageWMS: {
            const url = getAbsoluteUrl(node.mapService.url);
            if (node.mapServiceLayerIds) {
                url.searchParams.set('layers', node.mapServiceLayerIds);
            }
            else {
                console.warn(`[transformLayerConfigFromMapManager] missing required mapServiceLayerIds`, node);
            }
            return {
                type: LAYER_TYPE.IMAGE_WMS,
                id: node.nameId,
                auth: node.mapService.isAuth,
                legend,
                sourceOptions: {
                    url: hrefFromURL(url),
                    attributions: node.mapService.attributions,
                },
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.TILE: {
            mapServiceLayerIdsIgnored(node);
            if (!node.mapService.mapGrid) {
                console.error(`[transformLayerConfigFromMapManager]: mapGrid not defined`, node);
                return null;
            }
            const tileSize = node.mapService.mapGrid?.tileSize || undefined;
            return {
                type: LAYER_TYPE.TILE,
                id: node.nameId,
                auth: node.mapService.isAuth,
                legend,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions ? [node.mapService.attributions] : [],
                    tileSize: tileSize ? [tileSize, tileSize] : undefined,
                    // crossOrigin:
                    // 'anonymous' - obchazi CORSy, ale nejde tisknout
                    // '' - vyzaduje spravne nastavene CORSy, jinak se ani nezobrazi
                    crossOrigin: '',
                },
                gridParams: transformGridParams(node.mapService.mapGrid),
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.TileArcGIS: {
            mapServiceLayerIdsIgnored(node);
            if (!node.mapService.mapGrid) {
                console.error(`[transformLayerConfigFromMapManager]: mapGrid not defined`, node);
                return null;
            }
            return {
                type: LAYER_TYPE.TILE_ARCGIS,
                id: node.nameId,
                auth: node.mapService.isAuth,
                legend,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions,
                },
                gridParams: transformGridParams(node.mapService.mapGrid),
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.TileArcGISRest: {
            if (!node.mapService.mapGrid) {
                console.error(`[transformLayerConfigFromMapManager]: mapGrid not defined`, node);
                return null;
            }
            return {
                type: LAYER_TYPE.TILE_ARCGIS_REST,
                id: node.nameId,
                auth: node.mapService.isAuth,
                legend,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions,
                    params: {
                        layers: node.mapServiceLayerIds ? `show:${node.mapServiceLayerIds}` : undefined,
                    },
                },
                gridParams: transformGridParams(node.mapService.mapGrid),
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.Vector: {
            mapServiceLayerIdsIgnored(node);
            return {
                type: LAYER_TYPE.VECTOR,
                id: node.nameId,
                auth: node.mapService.isAuth,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions,
                    format: getVectorFormat(node.mapService.format),
                },
                features: [],
                mapboxStyle,
                legend: mapBoxStyleLegend,
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
        case MapServiceType.VectorTile: {
            if (!node.mapService.mapGrid) {
                console.error(`[transformLayerConfigFromMapManager]: mapGrid not defined`, node);
                return null;
            }
            return {
                type: LAYER_TYPE.TILE_VECTOR,
                id: node.nameId,
                auth: node.mapService.isAuth,
                sourceOptions: {
                    url: node.mapService.url,
                    params: {
                        layers: node.mapServiceLayerIds,
                    },
                    attributions: node.mapService.attributions ? [node.mapService.attributions] : [],
                },
                mapboxStyle,
                legend: mapBoxStyleLegend,
                routerParams,
                gridParams: transformGridParams(node.mapService.mapGrid),
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
                canInsert: node.canInsert,
                canUpdate: node.canUpdate,
                canDelete: node.canDelete,
            };
        }
        case MapServiceType.WMTS: {
            if (!node.mapService.mapGrid) {
                console.error(`[transformLayerConfigFromMapManager]: mapGrid not defined`, node);
                return null;
            }
            return {
                type: LAYER_TYPE.WMTS,
                id: node.nameId,
                auth: node.mapService.isAuth,
                legend,
                sourceOptions: {
                    url: node.mapService.url,
                    attributions: node.mapService.attributions ? [node.mapService.attributions] : [],
                    layer: node.mapServiceLayerIds,
                    matrixSet: node.mapService.matrixSet,
                    style: 'default',
                    requestEncoding: 'REST',
                },
                gridParams: transformGridParams(node.mapService.mapGrid),
                routerParams,
                layerOptions: {
                    visible: node.visible,
                    minResolution: node.minResolution,
                    maxResolution: node.maxResolution,
                    opacity: node.opacity,
                    zIndex: node.zIndex,
                },
            };
        }
    }
};
//# sourceMappingURL=transformLayerConfigFromTinker.js.map